import { Box, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { memo } from "react";
import { ReviewPeriodData } from "src/store/api/generatedApi";
import { useUserUid } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { getReviewPeriodColor } from "../utils/reviewPeriodColors";
import { TimelineSlot } from "./timelineLayout";

dayjs.extend(utc);

interface ReviewPeriodMarkersProps {
  reviewPeriods: ReviewPeriodData[];
  slot: TimelineSlot;
  getPositionPercent: (time: Dayjs) => number;
  suppressTooltip?: boolean;
}

export const ReviewPeriodMarkers = memo(
  ({
    reviewPeriods,
    slot,
    getPositionPercent,
    suppressTooltip = false,
  }: ReviewPeriodMarkersProps) => {
    return (
      <>
        {reviewPeriods.map((period) => (
          <ReviewPeriodMarker
            key={period.id}
            period={period}
            slot={slot}
            getPositionPercent={getPositionPercent}
            suppressTooltip={suppressTooltip}
          />
        ))}
      </>
    );
  }
);
ReviewPeriodMarkers.displayName = "ReviewPeriodMarkers";

const ReviewPeriodMarker = memo(
  ({
    period,
    slot,
    getPositionPercent,
    suppressTooltip,
  }: {
    period: ReviewPeriodData;
    slot: TimelineSlot;
    getPositionPercent: (time: Dayjs) => number;
    suppressTooltip: boolean;
  }) => {
    const { t } = useTenantTranslation();
    const userUid = useUserUid();
    const periodStart = dayjs(period.start);
    const periodEnd = dayjs(period.end);

    const leftPercent = getPositionPercent(periodStart);
    const widthPercent = getPositionPercent(periodEnd) - leftPercent;

    const verifiedAt = period.verified_at
      ? `${dayjs.utc(period.verified_at).format("YYYY-MM-DD HH:mm")} UTC`
      : null;
    const verifiedTooltip = verifiedAt
      ? period.verified_by_name
        ? `${t("verifiedBy", { name: period.verified_by_name })} · ${verifiedAt}`
        : verifiedAt
      : null;

    const tooltip = suppressTooltip ? null : verifiedTooltip;

    const marker = (
      <Box
        key={period.id}
        sx={{
          position: "absolute",
          left: `${leftPercent}%`,
          width: `${Math.max(widthPercent, 0.5)}%`,
          top: slot.top,
          height: slot.height,
          bgcolor: getReviewPeriodColor(period, userUid),
          opacity: 0.6,
          borderRadius: slot.borderRadius,
          pointerEvents: tooltip ? "auto" : "none",
          "&:hover": {
            opacity: 0.9,
          },
        }}
      />
    );

    return tooltip ? (
      <Tooltip title={tooltip} arrow>
        {marker}
      </Tooltip>
    ) : (
      marker
    );
  }
);
ReviewPeriodMarker.displayName = "ReviewPeriodMarker";
